import Domain from 'helpers/Domain';
import SmartTagCastAPI from '../shared/smart-tag-cast-api';
import { PinHelper } from 'helpers/PinHelper';
import { VideoPagePinTrackingService } from 'services/VideoPagePinTrackingService';
import DMAStorageService from 'services/DMAStorageService';
import DMAService from 'services/DMAService';
import { ErrorCopy } from 'video/errorCopy';
import { ErrorCopyConfig } from 'video/utils/error-copy';
import { getLocale } from 'services/LocalizationService';
import { fetchData } from 'video/shared/smart-tag-adapter/network-tools';
import { getCSID, getVideoAdLanguageSelection } from 'video/shared/smart-tag-adapter/ad-sessions';
import {
    getContinuousPlayEnabledStatus,
    formatEpisodesRemaining,
} from 'video/shared/smart-tag-adapter/continous-play';
import { SmartTagAdapterContextBuilder } from 'video/shared/smart-tag-adapter/context/smart-tag-adapter-context-builder';
import { isDVRStreamType } from 'video/shared/smart-tag-adapter/context/smart-tag-adapter-context-tools';
import { LOW_BIT_2MBPS } from 'helpers/bitrate';
import { isAuthenticated, isKidsVideo } from 'video/shared/smart-tag-adapter/video-tools';
import { CustomMeasure } from '@cbsi-cbscom/custom-measurement';
import { SMART_TAG_ADAPTER } from 'video/shared/smart-tag-adapter/measure-constants';
import {
    clearCardsDataFromUtag,
    saveSessionEndcardData,
} from './smart-tag-adapter/TrackingEndcardData';
import { getLiveKeyMomentsData } from 'video/shared/smart-tag-adapter/key-moments';
import { isNFL } from 'video/shared/trackingHelper';
import ConcurrencyManager from './smart-tag-adapter/ConcurrencyManager';

const ADULT = 'ADULT';
const SHOWTIME_BRAND = 'Showtime';
let Helper = window.Chromecast?.getHelper();
const SYNCBAK = 'syncbak';
const FAILED_TO_GET_SHOW = 'Failed to get show';
const FAILED_TO_GET_RECOMMENDATION = 'Failed to get recommendation';
const DAI_LIVE_PREFIX = 'DAI LIVE STREAM';
/** Time that will wait the service to be remaining to ask for a new token, if the time is bigger than this, it will return the same token because is still valid **/
const TIMEOUT_SYNCBAK = 10;

const customMeasure = new CustomMeasure({ namespace: SMART_TAG_ADAPTER.NAMESPACE });

export default class SmartTagAdapter {
    constructor(castSender, smartTagParams, metaData = null, apiMetadata = null) {
        this.channelInfo = {};
        this.smartTagParams = smartTagParams;
        this.playerInstance = null;
        this.smartTagVO = null;
        this.errorCopyConfig = new ErrorCopyConfig(CBS.Registry.brand.name);
        // for internal use, not referenced or reset by SmartTag
        this._contentId = this.smartTagParams._contentID;
        this.video_url = null;
        this.baseUrl = null;
        this.smartTagInstance = null;
        this.smartTagPlayerId = null;
        //this.enableCP = false;
        this.hasAdBlocker = false;
        this.castSender = castSender;
        this.showId = null;
        this.promisesToWaitFor = [];
        this.channelTitleName = null;
        this.metaData = metaData || {};
        this.CONTENT = this.smartTagParams.CONTENT || {};
        // getting pinCheck value for pinGatePromise decision logic
        this.pinCheck = this.smartTagParams.pinCheck;
        this.__cpPre = false;
        this.__cpsession = '0';
        this.videoTitle = this.metaData.videoTitle
            ? this.metaData.videoTitle
            : this.smartTagParams._videoTitle
              ? this.smartTagParams._videoTitle
              : '';
        this.contentType = this.smartTagParams.contentType;
        this.fch = this.smartTagParams.fch;
        this.onPlayerStateChanged = null;
        this.AD;
        this.redirectUrl = null;
        this.brandLogoUrl = null;
        this.apiMetadata = apiMetadata ? [apiMetadata] : [this.smartTagParams.apiMetadata];
        this.scheduleAPIData = null;
        //Keep the initial configuration of paramsVO to used in the replay
        this.initialParamsVO = null;
        this.lowbandEnabled = smartTagParams.lowbandEnabled || false;
        this.triggerPromisesOnInit = true;
        this.cpHBTracking = [];
        // GPP
        this.GPP;
        this.dma = 0;
        this.isContinousPlay = false;
        // look for playlistId query param, e.g. video config section ID
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        this.playlistId = parseInt(urlParams.get('playlistId'));
        if (isNaN(this.playlistId)) {
            this.playlistId = null;
        }
        this.playlistTitle = urlParams.get('playlistTitle');
        this.multiStream = true;
        this.multiStreamPlayerID = 0;
        this.multiStreamOpen = false;
        this.CPEpisodesRemainingEnabled = false;

        // Defer smart tag load() until utag is ready for tracking
        // Resolves race condition where no video tracking happens
        this.addPromiseToWaitFor(
            new Promise((resolve) => {
                const id = setTimeout(() => {
                    window.console.log('timeout reached for eventUtagReady');
                    resolve();
                }, 2000); // 1000 milliseconds = 1 second
                window.addEventListener('eventUtagReady', (event) => {
                    window.console.log('eventUtagReady event fired');
                    clearTimeout(id);
                    resolve();
                });
            }),
        );

        // Setting GPP params
        if (Domain.isDomestic() && window.CBS.Registry.gppPromise) {
            this.addPromiseToWaitFor(
                new Promise((resolve) => {
                    window.CBS.Registry.gppPromise
                        .then((GPPData) => {
                            this.GPP = {
                                gpp_sid: GPPData.gpp_sid,
                                gpp: GPPData.gpp,
                            };
                        })
                        .finally((result) => {
                            this.GPP = {
                                gpp_sid: this.GPP?.gpp_sid ?? 'undefined',
                                gpp: this.GPP?.gpp ?? 'undefined',
                            };
                            resolve();
                        });
                }),
            );
        }

        this.enableLiveDAI();

        // See if we have to wait for client-side AJAX of drm
        if (window.CBS.Registry.drmPromise) {
            this.addPromiseToWaitFor(
                new Promise((resolve, reject) => {
                    window.CBS.Registry.drmPromise.then(() => {
                        // install the drm object
                        this.CONTENT.drm = window.CBS.Registry.drm;
                        if (window.CBS.Registry.drmType)
                            this.CONTEXT.metaData.drmName =
                                window.CBS.Registry.drmType == 'widevine'
                                    ? 'Widevine'
                                    : 'PlayReady';
                        resolve();
                    });
                }),
            );
        }

        this.addPromiseToWaitFor(this.getbrandLogoUrl());
        this.addPromiseToWaitFor(this.getDma());

        this.AD = this.AD || {};
        const vodf_flag = CBS.Registry.Features?.cmafvod_workflow_enabled ?? false;
        this.AD.vodf = vodf_flag;

        // See if we have to wait for client-side AJAX of FreeWheel FMS data
        if (window.CBS.Registry.fmsPromise) {
            this.addPromiseToWaitFor(
                new Promise((resolve, reject) => {
                    window.CBS.Registry.fmsPromise.then(() => {
                        this.AD.FW = this.AD.FW || {};
                        this.AD.FW.FMS = window.CBS.Registry.fms;
                        if (window.CBS.Registry.GDPR) {
                            this.AD.FW.GDPR = window.CBS.Registry.GDPR;
                        }
                        resolve();
                    });
                }),
            );
        }

        if (Domain.isInternational() && window.CBS.Registry.OptanonNPAFlagPromise) {
            this.addPromiseToWaitFor(
                new Promise((resolve) => {
                    window.CBS.Registry.OptanonNPAFlagPromise.then((NPAFlag) => {
                        this.AD = this.AD || {};
                        this.AD.FW = {
                            NPA: {
                                flag: NPAFlag,
                            },
                            ...this.AD.FW,
                        };
                        resolve();
                    });
                }),
            );
        }

        this.playerLoadPromise = new Promise((resolve, reject) => {
            this.playerLoadResolver = resolve;
        });

        this.smartTagReadyPromise = new Promise((resolve, reject) => {
            this.smartTagLoadResolver = resolve;
        });

        // using this for startCastSender() when pinGate is enabled
        this.pinGatePromise = new Promise((resolve, reject) => {
            this.pinGateResolver = resolve;
        });

        // resolve pinGatePromise immediately if pinCheck param is false (pinGate is off)
        if (!this.pinCheck) {
            this.pinGateResolver(true);
        }

        this.api = $.extend(castSender || {}, {
            tagCallback: (event) => {
                const tagEvent = new CustomEvent(event.type, {
                    detail: {
                        data: event.data,
                        tag: event.tag,
                    },
                });

                // broadcast for vue event listeners
                document.dispatchEvent(tagEvent);
                this.tagCallback(event);
            },
            tagAction: (data) => {
                // get result of tagAction
                let result = this.tagAction(data);
                // if result of tagAction processing is a Promise => return that Promise...
                if (
                    !!result &&
                    (typeof result === 'object' || typeof result === 'function') &&
                    typeof result.then === 'function'
                ) {
                    return result;
                } else {
                    // otherwise
                    // return undefined or any other value we may have in the future...
                    return undefined;
                }
            },
            // this gets called called when smart tag library is ready
            onSmartTagReady: (smartTag) => {
                this.onSmartTagReady(smartTag);
            },
            ContinuousPlayNS: {
                saveContinuousPlaySessionData: (objMediaTrackingMetadata) => {
                    this.saveContinuousPlaySessionData(objMediaTrackingMetadata);
                },
                continuousPlayNextVideoMetadata: () => {
                    this.continuousPlayNextVideoMetadata();
                },
                eraseContinuousPlaySessionData: () => {
                    this.eraseContinuousPlaySessionData();
                },
            },
            CastNS: {
                // defined in smart-tag-cast-api.js
            },
            PINGateNS: {
                onPinAction: (obj) => {
                    this.onPinAction(obj);
                },
            },
            SkinNS: {
                replayResource: (data) => {
                    this.reloadVideo();
                },
                skinAction: (data) => {
                    return this.skinAction(data);
                },
                getScheduleData: () => {
                    return this.scheduleAPIData;
                },
            },
            onSmartTagAction: (event) => {
                this.onSmartTagAction(event);
            },
            CastToLocalAdapter: {},
            pollABState: () => {
                return this.pollABState();
            },
            pageAction: (action) => {
                // possible race condition? check for null: CBSSITE-10928
                if (this.smartTagVO) {
                    this.smartTagVO.API.SKIN.pageAction(action);
                }
            },
            checkContext: (context) => {
                return context.country !== 'us';
            },
        });
    }

    skinAction(data) {
        switch (data.action) {
            case 'LiveScheduleAction.GET_LIVE_SCHEDULE':
                return this.getScheduleData(data);
            case 'LiveScheduleAction.GET_CHANNEL_SCHEDULE':
                return new Promise(function (resolve, reject) {
                    let storageStationId = localStorage.getItem('stationIdLocation');
                    let url =
                        CBS.Registry.region.prefix +
                        '/live-tv/stream/channelSchedulePlayer/' +
                        data.payload.slug.data +
                        '/schedule.json?stationId=' +
                        storageStationId +
                        '&timestamp=' +
                        new Date().getTime();
                    return fetchData(url)
                        .then(function (response) {
                            return response.json();
                        })
                        .then(function (resultData) {
                            if (resultData.success) {
                                resolve({ data: resultData.result });
                            } else {
                                resolve({ data: resultData });
                            }
                        });
                });
            case 'SkinAction.GET_ENDCARD_DATA':
                // Returns continuous play / end card object for shows, movies, clips, trailers and  Live-tv
                return this.getEndCardData(data);

            case 'SkinAction.GET_MIDCARD_DATA':
            case 'SkinAction.GET_MIDCARD_RECOMMENDATION':
                if (CBS.Registry?.Features?.midcard_upnext_enabled) {
                    return this.getEndCardData(data);
                }
                let seriesId = CBS.Registry?.Midcards?.midcard_seriesid ?? null;
                if (!seriesId) {
                    return Promise.reject('SeriesId is invalid');
                }
                return new Promise((resolve, reject) => {
                    let url = `${CBS.Registry.region.prefix}/video/midcard/get-pinned-series.json?infoid=${seriesId}`;
                    fetch(url)
                        .then((response) => {
                            return response.json();
                        })
                        .then((response) => {
                            if (!response?.success) {
                                reject(FAILED_TO_GET_RECOMMENDATION);
                            }
                            return resolve(response);
                        })
                        .catch((error) => {
                            console.log(FAILED_TO_GET_RECOMMENDATION, error);
                            reject(FAILED_TO_GET_RECOMMENDATION);
                        });
                });
            case 'SkinAction.GET_MIDCARD_REDIRECT':
                seriesId = CBS.Registry?.Midcards?.midcard_seriesid ?? null;
                if (!seriesId) {
                    return Promise.reject('SeriesId is invalid');
                }
                return new Promise((resolve, reject) => {
                    let url = `${CBS.Registry.region.prefix}/video/midcard/get-redirect.json?infoid=${seriesId}`;
                    fetch(url)
                        .then((response) => {
                            return response.json();
                        })
                        .then((response) => {
                            if (!response?.success) {
                                return reject(FAILED_TO_GET_SHOW);
                            }
                            return resolve(response);
                        })
                        .catch((error) => {
                            console.log(FAILED_TO_GET_SHOW, error);
                            return reject(FAILED_TO_GET_SHOW);
                        });
                });
            case 'LiveScheduleAction.GET_CHANNEL_FILTERS':
                let url = CBS.Registry.region.prefix + '/live-tv/stream/channelCategories.json';
                if (CBS.Registry.user.bundleStatus ?? false) {
                    url += '?bundleStatus=' + CBS.Registry.user.bundleStatus;
                }
                return new Promise((resolve, reject) => {
                    return fetchData(url)
                        .then((response) => {
                            return response.json();
                        })
                        .then((json) => {
                            resolve({
                                data: json,
                            });
                        })
                        .catch((error) => {
                            console.log(error);
                            reject(error);
                        });
                });

            case 'LiveScheduleAction.UPDATE_CHANNEL_FILTERS':
                return new Promise((resolve, reject) => {
                    localStorage.setItem('channelBrowseCategory', data.payload.filters ?? 'all');
                    resolve();
                });

            case 'LiveScheduleAction.LIVE_CHANNEL_CLICK':
                localStorage.setItem('nextGenMenuClicked', true);
                return new Promise((resolve, reject) => {
                    localStorage.setItem('cbs_nsr', 1);
                    resolve();
                });
            case 'MultiStreamAction.MULTI_STREAM_CLICK':
                return this.handleMultiStreamClick(data);
            case 'MultiStreamAction.GET_CHANNEL_LOAD_METADATA':
                this.multiStream = true;
                this.multiStreamPlayerID = data?.payload?.playerId ?? 0;
                return this.getOrLoadChannel(data, false);
            case 'LiveScheduleAction.CHANGE_CHANNEL':
                return this.getOrLoadChannel(data);

            case 'SkinAction.UPDATE_NFL_OPTIN_VALUE':
                return new Promise((resolve, reject) => {
                    let url =
                        CBS.Registry.region.prefix +
                        '/user-profile/xhr/save-nfl-optin/?nflOptIn=true';
                    fetchData(url)
                        .then((response) => {
                            if (!response.ok) {
                                throw new Error(`HTTP error! status: ${response.status}`);
                            }
                            return response.json(); // This returns a promise
                        })
                        .then((data) => {
                            // Check the outer and inner success properties
                            if (data.success) {
                                this.smartTagVO.params.USER.nflLTS.optIn = data.optIn;
                                this.smartTagVO.params.USER.nflLTS.optInTime = data.optInTime;
                                resolve(data);
                            } else {
                                console.log('An error occurred while saving nflOptIn.');
                                reject(data);
                            }
                        })
                        .catch((error) => {
                            console.error(
                                'An error occurred while making the request to save nflOptIn: ',
                                error,
                            );
                            reject(data);
                        });
                });
            case 'RelatedVideosAction.GET_RELATED_VIDEOS':
                const gameID = data.payload.contentId;
                const regionPrefix = CBS.Registry.region.prefix;
                return getLiveKeyMomentsData(gameID, regionPrefix);
            case 'SkinAction.REFRESH_SYNCBAK_TOKEN':
                return new Promise((resolve, reject) => {
                    let response = {
                        success: false,
                        assetURL: '',
                        tokenExpirationTime: null,
                        message: '',
                    };
                    const refreshListing = data.payload.listing;
                    const initialTokenExpirationTime =
                        this.smartTagVO.params.CONTENT.tokenExpirationTime;

                    function updateContentAndResponse(content, dma) {
                        content.assetURL = dma.assetURL;
                        content.tokenExpirationTime = dma.tokenExpirationTime;
                        response.assetURL = dma.assetURL;
                        response.tokenExpirationTime = dma.tokenExpirationTime;
                    }

                    if (refreshListing.streamType === SYNCBAK) {
                        const tokenExpirationDiff =
                            initialTokenExpirationTime - Math.floor(Date.now() / 1000);
                        if (tokenExpirationDiff > TIMEOUT_SYNCBAK) {
                            // Updating the response when the token hasn't expired yet
                            updateContentAndResponse(
                                this.smartTagVO.params.CONTENT,
                                this.smartTagVO.params.CONTENT,
                            );
                            response.success = true;
                            resolve(response);
                        } else {
                            let dmaService = new DMAService();
                            const isLTSEnabled = refreshListing.channelTypes.includes('event_lts');
                            dmaService
                                .getRefreshedTokenFromDma(isLTSEnabled)
                                .then((dma) => {
                                    if (dma.success) {
                                        // Updating the response when dma is successful
                                        updateContentAndResponse(
                                            this.smartTagVO.params.CONTENT,
                                            dma,
                                        );
                                        response.success = true;
                                        resolve(response);
                                    } else {
                                        response.success = false;
                                        response.message = 'DMA not found';
                                        console.log(response.message);
                                        reject(response);
                                    }
                                })
                                .catch((error) => {
                                    response.success = false;
                                    response.message = 'Error while refreshing token';
                                    console.log(response.message, error);
                                    reject(response);
                                });
                        }
                    } else {
                        response.success = false;
                        response.message = 'Not a syncbak channel';
                        console.log(response.message);
                        reject(response);
                    }
                });
        }
    }

    addPromiseToWaitFor(promise) {
        if (promise && promise instanceof Promise) {
            this.promisesToWaitFor.push(promise);
        }
    }

    initContinuousPlayTracking() {
        try {
            let cp = JSON.parse(localStorage.getItem('cbs_cp'))?.cp || {};
            let endcardContentId = JSON.parse(sessionStorage.getItem('contentID')) || {};
            if (
                endcardContentId !== this?.metaData?.contentId &&
                endcardContentId !== this?.apiMetadata[0]?.contentId
            ) {
                cp = {};
            }
            this.isContinousPlay = endcardContentId == this?.apiMetadata[0]?.contentId ?? false;

            Object.keys(cp).forEach((key) => {
                this.cpHBTracking[key] = cp[key];
                if (key === 'contPlayState') {
                    this.__cpPre = true;
                    this.__cpsession = '1';
                }
            });
        } catch (e) {
            console.log(e);
        }
        this.eraseContinuousPlaySessionData(); // Clean up CP session data
    }

    onPinAction(obj) {
        let tracking = null;
        if (CBS.Registry.parentalControlTracking !== undefined) {
            // AU, CA
            tracking = new CBS.Registry.parentalControlTracking();
        } else {
            // Domestic
            tracking = new VideoPagePinTrackingService();
        }
        // Parse SmartTag call payload (decode payload from base64 to object if base64 format is detected)
        let payload = this.isBase64(obj.payload) ? this.decodeBase64(obj.payload) : obj.payload;
        switch (payload.action) {
            case 'Display':
                tracking && tracking.pinView();
                break;
            case 'Check':
                if (payload.pin !== undefined) {
                    let promise = PinHelper.validatePin(payload.pin, this._contentId);
                    promise.then(
                        (isvalid) => {
                            if (isvalid) {
                                tracking && tracking.pinOk();
                                this.pinGateResolver(true);
                            } else {
                                tracking && tracking.pinFail();
                            }
                            obj.callback(isvalid);
                        },
                        function () {
                            obj.callback(false);
                        },
                    );
                }
                break;
            case 'Cancel':
                tracking && tracking.pinCancel();
                // AU/CA cancel: redirect to home page
                window.location = '/';
                break;
            case 'Forgot':
                tracking && tracking.pinForgot();
                window.location = '/account/#forgot-pin';
                break;
        }
        tracking.trackState({});
    }

    /**
     * Determine if a string is base64 encoded
     * @param string str. The string
     * @returns boolean
     */
    isBase64(str) {
        return (
            str.match(
                new RegExp(
                    '^([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{4}|[A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{2}==)$',
                ),
            ) !== null
        );
    }

    /**
     * Decode base 64 string
     * @param string str - the encoded string
     * @returns string - decoded string
     */
    decodeBase64(str) {
        return JSON.parse(atob(decodeURI(str)));
    }

    /**
     * Function called by smartTag asking page to erase CP local storage session data
     * when next CP video metadata is unavailable
     */
    eraseContinuousPlaySessionData() {
        localStorage.setItem('cbs_cp', JSON.stringify({}));
        document.cookie = 'CBS_CP=0;path=/';
    }

    /**
     * @description Callback method called by the individual Tag application created in onSmartTagReady
     * @param {{type:String, tag:Tag}} event
     */
    tagCallback(event) {
        switch (event.type) {
            case 'TagEvent.ON_CREATE_PLAYER_REQUEST':
                if (typeof vtg !== 'undefined') {
                    vtg.config.Sparrow.params.serverInfo.serverUrl =
                        CBS.Registry.sparrowHost.replace(/(^\w+:|^)\/\//, '');
                }
                break;
            case 'TagEvent.ON_READY':
                this.smartTagVO = event.tag; // store Tag application reference
                // request Tag to load the content
                this.smartTagLoadResolver(true);
                if (this.smartTagParams.showSkinSideBar) {
                    this.activateSkinSideBar();
                }

                Promise.all(this.promisesToWaitFor).then(() => {
                    let paramsVO = null;
                    // if playready drm (e.g. Edge Chromium on Windows 10): Bug in CVP's default Shaka player adaptor
                    // if (typeof this.CONTENT.drm !== 'undefined' && typeof this.CONTENT.drm.playready !== 'undefined' && !$.isEmptyObject(this.CONTENT.drm.playready) ) {
                    if (
                        typeof window.CBS.Registry.drmPromise !== 'undefined' &&
                        window.CBS.Registry.drmPromise !== null
                    ) {
                        paramsVO = $.extend(true, paramsVO, { CONTENT: { drm: this.CONTENT.drm } });
                    }

                    if (utag_data) {
                        paramsVO = $.extend(true, paramsVO, { CONTEXT: { metadata: utag_data } });
                    }

                    // Note, for now, load() CANNOT take null else it will nuke all the params - might be fixed?
                    if (paramsVO !== null) {
                        this.smartTagVO.API.VIDEO.load(paramsVO);
                    } else {
                        this.smartTagVO.API.VIDEO.load();
                    }

                    if (CBS.Registry.Features.concurrency_3_1_enabled ?? false) {
                        ConcurrencyManager.concurrencyCheck(this._contentId, this.smartTagVO);
                    }
                });

                console.log('CAST: calling initializeCaster from TagEvent.ON_READY');
                this.initializeCaster();
                this.playerLoadResolver(true);
                break;

            // this appears to be for replay "end card"?
            case 'SkinEvent.ON_REPLAY_REQUEST':
                this.reloadVideo();
                break;

            // this appears to be for Live TV replay after timeoout
            case 'TagEvent.ON_API_REPLAY_VIDEO_REQUEST':
                this.reloadVideo();
                break;

            case 'TagEvent.ON_PLAYER_EVENT':
                switch (event.data.playerEvent.type ?? '') {
                    case 'contentplaying':
                        if (CBS.Registry.Features.concurrency_3_1_enabled ?? false == true) {
                            ConcurrencyManager.playerStateChange(event.data.playerEvent.type);
                        }
                        break;
                    case 'PlayerEvent.TAG_PLAYER_STATE_CHANGE':
                        if (typeof this.onPlayerStateChanged === 'function') {
                            this.onPlayerStateChanged(event.data.playerEvent.data.playerState);
                        }
                        break;

                    // Event for when the video starts playing
                    case 'contentstart':
                        // Calling braze custom event

                        const show = utag_data ? utag_data.show : undefined;
                        const episodeTitle = utag_data ? utag_data.episodeTitle : undefined;

                        if (CBS.Registry.Features.braze_sdk === true)
                            import('shared/braze_events/events').then(({ onViewVideo }) => {
                                onViewVideo(show, episodeTitle);
                            });
                        break;
                }
                break;
        }
    }

    initializeCaster() {
        console.log('CAST: initializeCaster()');
        let isEdgeChromium = window.navigator.userAgent.toLowerCase().indexOf('edg/') > -1;
        let isChrome = window.navigator.userAgent.toLowerCase().indexOf('chrome/') > -1;
        if (isChrome || isEdgeChromium)
            $.getScript(
                'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1',
            );

        if (this.castSender) {
            console.log('CAST: new SmartTagCastAPI');
            this.api.CastToLocalAdapter = new SmartTagCastAPI(this.smartTagVO);
            console.log('CAST: this.api.CastToLocalAdapter.ready();');
            this.api.CastToLocalAdapter.ready();
        }
    }

    tagAction = (data) => {
        switch (data.action) {
            case 'TagAction.GET_SESSION_DATA':
                return new Promise((resolve, reject) => {
                    resolve({ sessionData: this.getVideoAdSessionValues() });
                });
        }
    };

    getAdapterContextBuilder(paramsVO) {
        return SmartTagAdapterContextBuilder.createInstance(paramsVO)
            .setMetaData(this.metaData)
            .setSmartTagParams(this.smartTagParams)
            .setContent(this.CONTENT)
            .setUtagData(utag_data)
            .setCpHBTracking(this.cpHBTracking)
            .setAPIMetaData(this.apiMetadata)
            .setShowId(this.showId)
            .setVideoTitle(this.videoTitle)
            .setDma(this.dma)
            .setCarouselId(this.playlistId);
    }

    /**
     * @description SmartTag ready callback. Called upon SmartTag application load and initialization success
     * @param {SmartTag} smartTag
     */
    onSmartTagReady = async (smartTag) => {
        customMeasure.start(SMART_TAG_ADAPTER.INIT);
        const readyEvent = new CustomEvent('SMART_TAG_ADAPTER.READY', {
            detail: {
                adapterInstance: this,
            },
        });
        document.dispatchEvent(readyEvent);
        this.initContinuousPlayTracking();

        let paramsVO = await this.initializeSmartTagParameters();
        this.updateUtagData();

        const contextBuilder = this.getAdapterContextBuilder(paramsVO);
        contextBuilder.computeData(paramsVO?.CONTENT?.apiMetadata[0]?.contentId);

        if (!this.triggerPromisesOnInit) {
            this.finalizeParamsVO(smartTag, paramsVO);
        } else {
            Promise.all(this.promisesToWaitFor).then((extraParams) => {
                extraParams.forEach((params) => {
                    $.extend(true, paramsVO, params);
                });
                this.finalizeParamsVO(smartTag, paramsVO);
            });
        }
    };

    finalizeParamsVO(smartTag, paramsVO) {
        $.extend(true, paramsVO.AD, this.AD);
        this.initialParamsVO = $.extend(true, this.initialParamsVO, paramsVO);

        smartTag.create(paramsVO);
        customMeasure.end(SMART_TAG_ADAPTER.INIT);
        customMeasure.sendEvent({
            eventName: SMART_TAG_ADAPTER.INIT,
        });
    }

    isKidsVideo() {
        return isKidsVideo(this.metaData.videoProperties);
    }

    async initializeSmartTagParameters() {
        let utagMetadata = utag_data ? utag_data : {};

        window.localStorage.setItem(
            'set_continuous_movie',
            JSON.stringify({ continuous_movie: true }),
        );

        var _cp = undefined;
        var _fch = undefined;
        if (this.fch) {
            _fch = {
                ...this.fch,
                endCreditsChapterTime: this.metaData.endCreditsChapterTime || '',
                contentType: this.contentType,
            };
        } else {
            // Default to 30 seconds before end of video
            let endCreditsChapterTime = this.metaData.videoLength - 30;
            //TODO: In the future, enable the "CP" property for live TV to be checked in PHP, similar to other types of content.
            _cp = {
                enabled: getContinuousPlayEnabledStatus(
                    this.smartTagParams.isLive ? true : this.enableCP,
                ),
                endCreditsChapterTime: this.metaData.endCreditsChapterTime || endCreditsChapterTime,
                countdownTime: this.showId && this.showId != -1 ? 10 : 30,
            };
            if (this.playlistId) {
                _cp.playlistId = parseInt(this.playlistId);
            }
            if (this.playlistTitle) {
                _cp.playlistTitle = this.playlistTitle;
            }
        }

        // is NextUI active ?
        let nextUiActive = this.smartTagParams.showSkinSideBar ?? false;
        // set Convia A/B testBucket value
        utagMetadata.testBucket = this.smartTagParams.testBucket;

        // disable pause ads live streams
        // disable ads from Paramount+ for user tier not TIER_CF/0
        // and any vod assets which has videoProperties "Kids"
        // and also disable for Ads for user profile type is either  "KIDS" or "YOUNGER_KIDS"
        // and clips and trailers

        let pauseAd = true;
        const { isLive } = this.smartTagParams;
        const { brand, user } = CBS.Registry;
        if (
            isLive ||
            this.isKidsVideo() ||
            this.apiMetadata[0]?.mediaType?.toLowerCase() === 'clip' ||
            this.apiMetadata[0]?.mediaType?.toLowerCase() === 'trailer'
        ) {
            pauseAd = false;
        } else if (isAuthenticated() && brand.name === 'PPLUS') {
            const { profile_type, isLC, isLCP } = user;
            const isCF = !isLC && !isLCP;
            if (profile_type === 'KIDS' || profile_type === 'YOUNGER_KIDS' || isCF) {
                pauseAd = false;
            }
        }
        // setup Tag parameters object
        let paramsVO = {
            // tag application parameters
            TAG: {
                container: $('cbsplayer'),
                callback: this.smartTagParams.callback,
                namespace: {
                    TAG: this.smartTagParams.jsNS,
                    CAST: this.smartTagParams.castNS, // Chromecast namespace. Tag is going to call Chromecast related methods on this namespace
                    PINGate: this.smartTagParams.PINGateNS, // PIN Gate namespace. Tag is going to call PIN Gate related methods on this namespace
                    SKIN: this.smartTagParams.skinNS,
                    CP: this.api.ContinuousPlayNS,
                },
                config: {
                    tag: [
                        {
                            ErrorCopy: ErrorCopy,
                        },
                    ],
                    skin: [
                        {
                            SkinPluginMultiStream: {
                                enabled:
                                    Domain.isDomestic() &&
                                    (CBS.Registry.Features?.web_multiview_enabled ?? false),
                                params: {
                                    config: {},
                                },
                            },
                        },
                        {
                            SkinSideBarPlugin: {
                                enabled: nextUiActive,
                                params: {
                                    config: {
                                        isSPA: this.smartTagParams.skinSideBarIsSPA ?? false,
                                        localTvLockOverride: true,
                                    },
                                },
                            },
                            EndCards: {
                                enabled: true,
                                params: {
                                    recommendationContextEnabled:
                                        CBS.Registry.Features?.uec_recommendation_context_enabled ??
                                        false,
                                    recommendationContextText: getLocale('recommended_for_you'),
                                    getNextChannelEnabled:
                                        CBS.Registry.Features?.live_tv_end_card ?? false,
                                    ID3enabled:
                                        CBS.Registry.Features?.id3_endcards_enabled ?? false,
                                    maxRandomDelay:
                                        CBS.Registry?.uec_broadcast_end_max_random_delay ?? 0,
                                    redirectFallbackEnabled: true,
                                    redirectFallbackDelay:
                                        CBS.Registry.video_redirect_fallback_delay ?? 15000,
                                },
                            },
                            MidCards: {
                                enabled: true,
                                params: {
                                    // TODO: review this for typecasting, maybe we need to consider the type of the data
                                    inactivityTimeout:
                                        CBS.Registry?.Midcards?.midcard_timeout_duration ?? '',
                                    ID3enabled: CBS.Registry.Features.id3_endcards_enabled ?? false,
                                    seriesId: CBS.Registry?.Midcards?.midcard_seriesid ?? '',
                                    maxRandomDelay:
                                        CBS.Registry?.Midcards?.midcard_max_random_delay ?? '',
                                    header: getLocale('midcard_header'),
                                    keepWatchingButton: getLocale('midcard_keep_watching_button'),
                                    watchNowButton: getLocale('midcard_watch_now_button'),
                                    useUpNext:
                                        CBS.Registry?.Features?.midcard_upnext_enabled ?? false,
                                },
                            },
                            PrePlayBack: {
                                enabled: true,
                                params: {
                                    controlsEnabled:
                                        CBS.Registry?.Features?.sfb_preplayback_enabled ?? false,
                                    panelEnabled:
                                        CBS.Registry?.Features?.sfb_player_control_enabled ?? false,
                                    seekOffset: CBS.Registry?.sfb?.sfb_seek_offset ?? false,
                                    maxSeekTime: CBS.Registry?.sfb?.sfb_max_seek_time ?? false,
                                },
                            },
                        },
                        {
                            Settings: {
                                params: {
                                    Controls: {
                                        ltsFeatureTooltip: {
                                            enabled: true,
                                        },
                                    },
                                },
                            },
                        },
                        {
                            PauseAd: {
                                enabled: pauseAd,
                                params: {
                                    provider: 'FREEWHEEL',
                                },
                            },
                        },
                        {
                            Copy: {
                                params: {
                                    Copy: {
                                        CONTROLS: {
                                            FULL_SCREEN: {
                                                TOOL_TIP: {
                                                    NORMAL: getLocale('full_screen'),
                                                    SELECTED: getLocale('exit_full_screen'),
                                                },
                                            },
                                            FF: {
                                                TOOL_TIP: {
                                                    NORMAL: getLocale('forward_num_sec'),
                                                    SELECTED: getLocale('forward_num_sec'),
                                                },
                                            },
                                            RW: {
                                                TOOL_TIP: {
                                                    NORMAL: getLocale('rewind_num_sec'),
                                                    SELECTED: getLocale('rewind_num_sec'),
                                                },
                                            },
                                            NEXT: {
                                                TOOL_TIP: {
                                                    SELECTED: getLocale('next_titlecase'),
                                                    NORMAL: getLocale('next_titlecase'),
                                                },
                                            },
                                            SKIP: {
                                                TOOL_TIP: {
                                                    NORMAL: getLocale('skip'),
                                                    SELECTED: getLocale('skip'),
                                                },
                                            },
                                            STATUS: {
                                                LIVE: getLocale('lts_status_live'),
                                            },
                                            DVR: {
                                                GO_LIVE: getLocale('lts_status_dvr'),
                                            },
                                            LTS_FEATURE_TOOLTIP: {
                                                MESSAGE: getLocale('lts_tooltip_text'),
                                                BUTTON: getLocale('lts_tooltip_button_text'),
                                            },
                                        },
                                        PRODUCT_PLACEMENT: {
                                            LABEL: getLocale('contains_product_placement'),
                                        },
                                        VOD_TIMEOUT: {
                                            STILL_WATCHING: getLocale(
                                                'still_watching_no_questionmark',
                                            ),
                                            YES_KEEP_WATCHING: getLocale('yes_keep_watching'),
                                            YES_DONT_ASK_AGAIN: getLocale(
                                                'yes_and_do_not_ask_me_again',
                                            ),
                                            NO_GOTO_HOMEPAGE: getLocale('no_go_to_homepage'),
                                        },
                                        END_DVR_LIVE_STREAM: {
                                            DVR_MSG: getLocale(
                                                'your_content_has_ended_but_theres_still_more_to_stream',
                                            ),
                                        },
                                        LANGUAGE: {
                                            CC: {
                                                SETTINGS: {
                                                    SELECTOR: {
                                                        POSITION: {
                                                            LABEL: getLocale('position'),
                                                            OPTIONS: {
                                                                CENTERED: getLocale('centered'),
                                                                DYNAMIC: getLocale('dynamic'),
                                                            },
                                                        },
                                                        ANIMATION: {
                                                            LABEL: getLocale('animation'),
                                                            OPTIONS: {
                                                                NONE: getLocale('none'),
                                                                SCROLL: getLocale('scroll'),
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                        AD_INFO: {
                                            HEADER: getLocale('advertisement'),
                                        },
                                    },
                                },
                            },
                        },
                    ],
                },
                playerOptions: {
                    networkTimeout: 10000,
                },
                loadingAnimation: true,
                loadingAnimationConfig: {
                    backgroundImage: {
                        enabled: true,
                    },
                    fadeIn: {
                        enabled: true,
                    },
                },
            },
            // ad related parameters
            AD: {
                AB: {
                    method: this.api.pollABState,
                    interval: 5000,
                    delay: 7000,
                },
                VIDEOADS: {
                    enabled: this.smartTagParams?.videoAds?.enabled || false,
                },
            },
            CONTENT: {
                metadata: [this.metaData] || [{}],
                apiMetadata: this.apiMetadata,
                assetURL: this.smartTagParams.assetURL || '',
                refIdForCast: this.smartTagParams._contentID || '',
                drm: this.CONTENT.drm || {},
                autoplay: this.smartTagParams.autoPlayVid || false,
                globalResumeTime: this.smartTagParams.isLive
                    ? NaN
                    : this.playlistId || this.isContinousPlay
                      ? 0
                      : this.smartTagParams.grestime || 0,
                partner: this.smartTagParams.partner || '',
                country: this.smartTagParams.userCountry || '',
                isLive: this.smartTagParams.isLive || false,
                resource: {
                    ad: {
                        ima: {
                            // extends stalled ad threshold to 30 seconds
                            stalledAdThreshold: 10,
                            // disables stalled ads check
                            enableStalledAdCheck: true,
                        },
                    },
                    playback: {
                        id3OwnerIds: [],
                    },
                    metadata: {
                        duration: this.apiMetadata[0].duration ?? 0,
                    },
                },
                channelInfo: CBS?.Registry?.channelInfo,
                playlistId: this.playlistId,
                playlistTitle: this.playlistTitle,
            },
            CONTEXT: {
                metadata: utagMetadata,
            },
            // skin related parameters
            SKIN: {
                // Start Uncommented for debugging purposes, e.g. very short timeouts for live TV to test "replay"
                // liveStreamTimeout: 5,
                // liveStreamTimeoutCountdown: 5,
                // End Uncommented for debugging purposes
                cp: _cp,
                fch: _fch,
                brandLogoUrl: this.smartTagParams.isLive ? '' : this.brandLogoUrl,
            },
            USER: {
                userStatus: this.smartTagParams.userStatus || CBS.Registry.user.sb,
                bundleStatus: this.smartTagParams.bundleStatus ?? '',
                packageSource: this.smartTagParams.packageSource ?? '',
                multiPackageTracking: this.smartTagParams.multiPackageTracking ?? '',
                userId: CBS.Registry.user.id || '',
                profileId: CBS.Registry.user.profile_id ?? '',
                mvpdId: this.smartTagParams.mvpdId || '',
                pinGate: this.smartTagParams.pinCheck, // enable / disable PIN Gate
                nflLTS: {
                    optIn: CBS.Registry.user.nflLTSOptIn,
                    optInTime: CBS.Registry.user.nflLTSOptInTime,
                },
            },
        };

        this.channelInfo = CBS?.Registry?.channelInfo;

        if (Domain.isDomestic()) {
            paramsVO.USER.usPrivacy = jQuery.cookie('usprivacy') ?? '';
        }

        if (this.lowbandEnabled) {
            paramsVO.CONTENT.resource.playback.abr = {
                maxBitrate: LOW_BIT_2MBPS,
            };
        }
        if (this.CONTENT.millstone !== undefined) {
            paramsVO.CONTENT.millstone = this.CONTENT.millstone ?? null;
        }

        if (Domain.isDomestic() || !(this.smartTagParams.isLive ?? false)) {
            paramsVO.CONTENT.pid = this.metaData.pid || '';
        }

        if (Domain.isInternational() && this.metaData.pid?.length > 0) {
            paramsVO.CONTENT.pid = this.smartTagParams.pid;
            delete paramsVO.CONTENT.assetURL;
        }

        if (this.smartTagParams.gameID) {
            paramsVO.TAG.config.skin[0]['SkinPluginRelatedVideos'] = {
                enabled: true,
                params: {
                    config: {
                        contentId: this.smartTagParams.gameID,
                        type: 'RelatedVideosType.LIVE_KEY_MOMENTS',
                        polling_interval: this.smartTagParams.keymoments_polling_interval,
                    },
                },
            };
        }

        if (Domain.isInternational()) {
            import(
                /* webpackChunkName: "skin-copy" */
                'video/skinCopy'
            ).then(({ SkinCopy }) => {
                paramsVO['TAG']['config']['skin'][0]['Copy'] = {
                    params: {
                        Copy: SkinCopy,
                    },
                };
            });
            import(
                /* webpackChunkName: "aria-labels" */
                'video/ariaLabels'
            ).then(({ AriaLabels }) => {
                paramsVO['TAG']['config']['skin'].push({
                    Aria: {
                        params: {
                            Aria: AriaLabels,
                        },
                    },
                });
            });
        }

        //pass daistreamKey as assetKey to smart tag for pid-based live streams (i.e. UEFA)
        // if (
        //     this.smartTagParams.isLive === true &&
        //     this.smartTagParams.pid &&
        //     this.metaData.daistreamKey
        // ) {
        //     paramsVO = $.extend(true, paramsVO, {
        //         CONTENT: {
        //             resource: { ad: { dai: { assetKey: this.metaData.daistreamKey } } }
        //         }
        //     });
        // }

        //accounts for uefa live streams within a VOD page that does not have liveStreamMetadata defined
        if (
            this.smartTagParams.isLive === true &&
            this.smartTagParams.liveStreamMetadata == undefined &&
            this.metaData
        ) {
            this.smartTagParams.liveStreamMetadata = this.metaData;
        }
        paramsVO.CONTENT.locale = CBS.Registry.region.locale;

        if (isDVRStreamType(this.metaData, CBS?.Registry?.channelInfo)) {
            paramsVO.CONTEXT.metadata.streamType = this.smartTagParams.streamType = 'DVR';
        }

        // only configure startTime if NOT a DVR stream
        if (
            !isDVRStreamType(this.metaData, CBS?.Registry?.channelInfo) &&
            this.smartTagParams.medTime
        ) {
            paramsVO.CONTENT.resource.playback.startTime = this.smartTagParams.medTime;
            if (this.playlistId) {
                paramsVO.CONTENT.resource.playback.startTime = 0;
            }
        }

        paramsVO.CONTENT.resource.playback.id3OwnerIds =
            this.smartTagParams.isLive === true ? ['www.nielsen.com'] : [];
        return paramsVO;
    }

    updateUtagData() {
        utag_data.isSPA = this.smartTagParams.skinSideBarIsSPA ?? false;
        utag_data.bundleStatus = this.smartTagParams.bundleStatus ?? '';
        utag_data.url = document.location.href;
        if (this.smartTagParams.isLive) {
            utag_data.pageType = 'video';
            if (localStorage.getItem('on_now_carousel')) {
                utag_data.channelBrowseCategory = 'all';
            }
            if (this.smartTagParams.channelName) {
                utag_data.rowHeaderTitle =
                    this.smartTagParams.channelName == 'CBS News Live Stream'
                        ? 'CBS News'
                        : this.smartTagParams.channelName;
            } else {
                utag_data.rowHeaderTitle =
                    this.smartTagParams.liveStreamMetadata?.channelName ??
                    this.smartTagParams.liveStreamMetadata?.label;
            }
        }
    }

    /**
     * @description This method is called by SmartTag, asking page to replay resource.
     * In Live assetURL type playback case => update assetURL and send updated resource config back to SmartTag for playback
     * @param {{callback:Function, resourceConfig: ResourceConfigurationInterface, updateResource:boolean}} data
     */
    replayResource = (data) => {
        //defined in LiveStreamPlayer.js
    };

    /**
     * Function called by smartTag for Continuous Play
     * @param {Object} objMediaTrackingMetadata
     */

    saveContinuousPlaySessionData(objMediaTrackingMetadata) {
        saveSessionEndcardData(objMediaTrackingMetadata);
    }

    /**
     * Function called by smartTag to get ad blocker status
     * @return {Boolean} true if ad blocker is installed, false if not installed
     */
    pollABState() {
        if (window._sp_ === undefined || CBS.Registry.user.sb.indexOf('sb|14') !== -1) {
            return false;
        } else {
            window._sp_.rerunDetection(); //reruns the detection
            window._sp_.isAdBlocking((isBlocking) => {
                this.hasAdBlocker = isBlocking;
            });
            return this.hasAdBlocker;
        }
    }

    /**
     * Function called when the smartTag and plugin is ready
     * @return {void}
     */
    activateSkinSideBar() {
        //Find a better way to do this.
        window.SmartTag.list[0].API.SKIN.pageAction({ action: 'PageAction.LIVE_MENU_CLICK' });
    }

    getVideoAdSessionValues = () => {
        let videoAdSessionValues = {
            session: '',
            subses: '',
            ge: '',
            gr: '',
            sub: '',
            ftag: '',
            tfcd: '',
            ppid: '',
            vguid: '',
            cpPre: '0',
            cpSession: this.__cpsession,
            vid: '',
            partner: '',
            host: '',
            sb: '',
            esb: '',
            tsb: '',
            ssb: '',
            ptype: '',
            device_region_language: '',
            _fw_h_x_country: '',
            language_selection: '',
            _fw_content_language: '',
            _fw_coppa: 0,
            region: '',
            source: '',
            showSection: '',
            movieSection: '',
            hubSlug: '',
            searchEventComplete: '',
            _fw_is_lat: 0,
            gpp_sid: '',
            gpp: '',
            billing: '',
            sublife: '',
            packageType: '',
            profile: '',
        };

        // Setting GPP params for DAI
        if (this?.GPP !== undefined) {
            videoAdSessionValues.gpp_sid = this?.GPP?.gpp_sid ?? gpp_sid;
            videoAdSessionValues.gpp = this?.GPP?.gpp ?? gpp;
        }

        if (this.fch) {
            videoAdSessionValues.section = 'free-content-hub';
        }
        let cbsAdvVal = jQuery.cookie('CBS_ADV_VAL');
        let cbsAdvSubsesVal = jQuery.cookie('CBS_ADV_SUBSES_VAL');

        if (cbsAdvVal != null && cbsAdvVal != '') videoAdSessionValues.session = cbsAdvVal;
        if (cbsAdvSubsesVal != null && cbsAdvSubsesVal != '')
            videoAdSessionValues.subses = cbsAdvSubsesVal;

        let cbs_u = jQuery.cookie('CBS_U');
        if (cbs_u != null) {
            cbs_u = cbs_u.replace(/"/g, '');
            let result = {};
            cbs_u.split('|').forEach(function (x) {
                let arr = x.split(':');
                arr[1] && (result[arr[0]] = arr[1]);
            });
            if (result.ge != 'null' && result.ge != '') videoAdSessionValues.ge = result.ge;
            if (result.gr != 'null' && result.gr != '') videoAdSessionValues.gr = result.gr;
        }

        let ftag = window.location.getParam('ftag');
        if (!ftag && window.location.hash) {
            const hash_sting = window.location.hash.substring(1);
            if (hash_sting.indexOf('ftag=') >= 0) {
                const parameters = new URLSearchParams(hash_sting);
                ftag = parameters.get('ftag');
                videoAdSessionValues.ftag = ftag;
            }
        }

        videoAdSessionValues.ppid =
            this.smartTagParams && this.smartTagParams.ppid ? this.smartTagParams.ppid : '';

        let profileType = CBS.Registry.user.profile_type || '';
        videoAdSessionValues.tfcd =
            profileType === 'KIDS' ||
            profileType === 'YOUNGER_KIDS' ||
            this.isKidsVideo() ||
            (this.smartTagParams.adCallParams &&
                this.smartTagParams.adCallParams.standardParams &&
                this.smartTagParams.adCallParams.standardParams.tfcd)
                ? 1
                : 0;
        videoAdSessionValues.vguid = window.__page_uuid ? __page_uuid : '';
        videoAdSessionValues.cpPre =
            this.smartTagParams &&
            this.smartTagParams.cpPre &&
            this.smartTagParams.cpPre === true &&
            this.__cpPre === true
                ? '1'
                : '0';
        videoAdSessionValues.cpSession = this.__cpsession || '0';
        videoAdSessionValues.sub =
            this.smartTagParams && this.smartTagParams.gusr && !this.isKidsVideo()
                ? this.smartTagParams.gusr
                : '';
        videoAdSessionValues.vid =
            this.smartTagParams && this.smartTagParams._contentID
                ? this.smartTagParams._contentID
                : '';
        videoAdSessionValues.partner =
            this.smartTagParams && this.smartTagParams.partner ? this.smartTagParams.partner : '';
        videoAdSessionValues.host = CBS.Registry.host ? CBS.Registry.host : '';
        videoAdSessionValues.ptype =
            window.utag_data && window.utag_data.pageType ? window.utag_data.pageType : '';

        const adsRegion = CBS.Registry?.user?.adsRegion?.toLowerCase() ?? '';
        this.appendVideoAdDAIParams(videoAdSessionValues, adsRegion);
        const vendorCode = CBS.Registry?.user?.vendorCode;
        videoAdSessionValues.csid = getCSID(this.smartTagParams.isLive, adsRegion, vendorCode);
        let userStatus = this.smartTagParams.userStatus;
        if (userStatus) {
            userStatus.split(',').forEach(function (x) {
                let arr = x.split('|');
                if (arr[1]) {
                    videoAdSessionValues[arr[0]] = arr[1];
                }
            });
        }
        videoAdSessionValues.sublife = CBS?.Registry?.user?.subscriptionLife ?? '';
        videoAdSessionValues.billing = CBS?.Registry?.user?.billingCadence ?? '';
        videoAdSessionValues.packageType = CBS?.Registry?.user?.packageSourceType ?? '';
        videoAdSessionValues.profile = CBS?.Registry?.user?.profile_id ?? '';

        return videoAdSessionValues;
    };

    appendVideoAdDAIParams(videoAdSessionValues, region) {
        videoAdSessionValues.source = window.utag_data?.rowHeaderTitle || '';
        videoAdSessionValues.showSection = window.utag_data?.showSection || '';
        videoAdSessionValues.movieSection = window.utag_data?.movieSection || '';
        videoAdSessionValues.hubSlug = window.utag_data?.hubSlug || '';
        videoAdSessionValues.searchEventComplete = window.utag_data?.searchEventComplete || '';

        if (window.utag_data?.endCardSourceType) {
            videoAdSessionValues.endcard = true;
            videoAdSessionValues.source = window.utag_data.endCardSourceType;
        }

        const locale = this.smartTagParams.defaultLocale ?? CBS.Registry?.region?.locale ?? '';
        const language_array = locale.split('-');
        const device_language = language_array?.[0];
        videoAdSessionValues.device_region_language = device_language || '';
        videoAdSessionValues._fw_h_x_country = region;
        videoAdSessionValues.region = region;
        videoAdSessionValues.language_selection = getVideoAdLanguageSelection() || device_language;
        videoAdSessionValues._fw_content_language = locale.split('-')[0];

        if (this.isKidsVideo()) {
            videoAdSessionValues.tfcd = 1;
            videoAdSessionValues._fw_coppa = 1;
            videoAdSessionValues.ge = '';
            videoAdSessionValues.gr = '';
        } else {
            videoAdSessionValues.tfcd = 0;
            videoAdSessionValues._fw_coppa = 0;
        }
        videoAdSessionValues._fw_is_lat = this.AD?.FW?.NPA?.flag || 0;
    }

    GetChromecastHelper() {
        if (!Helper) {
            Helper = window.Chromecast?.getHelper();
        }

        return Helper;
    }

    updateChromecastParams(listing, paramsVO) {
        if (!this.GetChromecastHelper()) {
            return;
        }

        if (!Helper?.getCoords()) {
            const geoLocation = JSON.parse(localStorage.getItem('storageLocation'));
            console.log('Attempting to use location', geoLocation);
            if (geoLocation) {
                Helper?.setLocation(localStorage);
            } else {
                console.warn(
                    'Could not to set location details: resources requiring location might fail to load',
                );
            }
        }
        const registry = Helper?.getRegistry();
        if (!registry || !this.smartTagVO.params) return;
        registry.paramsVO.setByPage.streamType = paramsVO.CONTEXT.metadata.stationCode
            ? null
            : 'etl';
        Helper.setRegistry(registry);
        paramsVO.CONTENT.stationName =
            listing.streamType === SYNCBAK ? paramsVO.CONTEXT.metadata.stationCode : null;
        Helper.updateTagVOProp('params', {
            ...this.smartTagVO.params,
            ...{
                CONTENT: { ...this.smartTagVO.params.CONTENT, ...paramsVO.CONTENT },
            },
        });
    }

    getbrandLogoUrl() {
        var slug = this.apiMetadata[0].brandSlug ?? null;
        //if brand slug is undefined
        if (!slug) {
            return;
        }
        var brandLogoKey = `Brand-${slug}`;
        var logoFetchUrl = `${CBS.Registry.region.prefix}/video/getbrand/${slug}/brand.json`;
        return new Promise((resolve, reject) => {
            if (localStorage.getItem(brandLogoKey)) {
                this.brandLogoUrl = localStorage.getItem(brandLogoKey);
                resolve();
                return;
            }
            return fetchData(logoFetchUrl)
                .then((response) => {
                    return response ? response.json() : false;
                })
                .then((json) => {
                    if (json) {
                        let brand = json.brand;
                        this.brandLogoUrl =
                            brand.isPlayerAttributionVisible && brand.filepathLogoRegularWeb
                                ? brand.filepathLogoRegularWeb
                                : '';
                        this.brandLogoUrl =
                            brand.isPlayerAttributionVisible && brand.filepathLogoRegularWeb
                                ? brand.filepathLogoRegularWeb
                                : '';
                        localStorage.setItem(brandLogoKey, this.brandLogoUrl);
                    } else {
                        localStorage.setItem(brandLogoKey, '');
                        this.brandLogoUrl = false;
                    }
                    resolve();
                })
                .catch((error) => {
                    console.log(error);
                    reject(error);
                });
        });
    }

    getEndCardData(data) {
        // Returns continuous play / end card object for shows, movies, clips, trailers and  Live-tv
        const nextItem = data?.payload?.nextItem;
        const channelObj = data?.payload?.channelObj;
        const contentType = (
            channelObj ? 'live' : (data?.payload?.contentType ?? '')
        ).toLowerCase();

        if (!contentType || !['show', 'movie', 'live', 'clip', 'trailer'].includes(contentType)) {
            return Promise.reject('ContentType is invalid');
        }
        if (ConcurrencyManager.isStopped()) {
            return Promise.reject('Concurrency Limit Reached; End card disabled');
        }
        let endCardId;
        let channelSlug = '';
        let dma = '';
        let listId;
        switch (contentType) {
            case 'live':
                endCardId = data.payload.listingId ?? null;
                channelSlug = data.payload.channelSlug ?? null;
                dma = this.dma;

                if (this.channelInfo && this.channelInfo?.slug) {
                    if (
                        this.channelInfo.currentListing &&
                        Array.isArray(this.channelInfo.currentListing) &&
                        this.channelInfo.currentListing.length > 0
                    ) {
                        listId = this.channelInfo.currentListing[0].id;
                    }
                    if (!channelSlug || channelSlug !== this.channelInfo.slug) {
                        channelSlug = this.channelInfo.slug;
                        endCardId = listId;
                    }

                    if (!endCardId) {
                        endCardId = listId;
                    }
                }
                break;
            case 'show':
                endCardId = this.showId ?? null;
                break;
            case 'movie':
            case 'clip':
            case 'trailer':
                endCardId = data?.payload?.contentId ?? this.contentId ?? null;
                break;
        }

        if (!endCardId) {
            return Promise.reject('Invalid endcardId');
        }

        return new Promise((resolve, reject) => {
            const abtest = window.nextShowsAbTest === true ? 1 : 0;
            const mediaType = this.metaData.mediaType ?? '';
            let url =
                contentType === 'live'
                    ? `${CBS.Registry.region.prefix}/video/endcard/up-next.json?contentType=${contentType}&endCardId=${endCardId}&channelSlug=${channelSlug}&dma=${dma}&nextItem=${nextItem}`
                    : `${CBS.Registry.region.prefix}/video/endcard/up-next.json?contentType=${contentType}&endCardId=${endCardId}&contentId=${this._contentId}&mediaType=${mediaType}&nextItem=${nextItem}&var=${abtest}`;
            if (this.playlistId) {
                url += `&playlistId=${this.playlistId}`;
            }
            if (this.playlistId) {
                url += `&playlistTitle=${encodeURIComponent(this.playlistTitle)}`;
            }
            return fetch(url)
                .then((response) => {
                    return response.json();
                })
                .then((response) => {
                    if (
                        !response.result?.apiResponse ||
                        response.result?.apiResponse?.recommendations?.length == 0
                    ) {
                        reject('Empty up-next API Response');
                    } else {
                        if (contentType !== 'live') {
                            const recommendations = response.result?.apiResponse?.recommendations;
                            response.result.episodesRemaining = formatEpisodesRemaining(
                                this.CPEpisodesRemainingEnabled,
                                recommendations,
                            );
                        }
                        resolve(response.result);
                    }
                })
                .catch((error) => {
                    console.log(error, url);
                    error.message = `${error.message}. ${url}`;
                    reject(error);
                });
        });
    }

    enableLiveDAI() {
        if (Domain.isInternational() && this.isLiveDAIStream()) {
            this.smartTagParams.videoAds.enabled = true;
            this.smartTagParams.videoAds.prefixOverwrite = true;
        }
    }

    isLiveDAIStream() {
        let isLiveDai = false;
        if (Array.isArray(this.apiMetadata?.[0]?.videoProperties)) {
            this.apiMetadata[0].videoProperties.every((property) => {
                if (property?.toUpperCase() === DAI_LIVE_PREFIX) {
                    isLiveDai = true;
                    return false;
                }
                return true;
            });
        }
        return isLiveDai;
    }

    async reloadVideo() {
        if (this.initialParamsVO.CONTENT?.channelInfo?.streamType == SYNCBAK) {
            // Get a new syncbak asset url/token
            let dmaService = new DMAService();
            dmaService
                .getRefreshedTokenFromDma(true)
                .then((dma) => {
                    if (dma.success) {
                        this.initialParamsVO = $.extend(true, this.initialParamsVO, {
                            CONTENT: {
                                assetURL: dma.assetURL,
                            },
                        });
                        this.smartTagVO.API.VIDEO.load(this.initialParamsVO);
                    } else {
                        console.log('DMA not found - cannot refresh syncbak token');
                    }
                })
                .catch((error) => {
                    console.log('Error while refreshing syncbak token');
                });
        } else {
            // TODO refresh DRM license? - it is most likely that this will only be used on clips/trailers without drm
            this.smartTagVO.API.VIDEO.load(this.initialParamsVO);
        }
    }

    async getDma() {
        const dma = await new DMAService().getDmaAndStationId();
        this.dma = dma[0] ?? 0;
    }

    getOrLoadChannel(data, reload = true) {
        const self = this;
        // load the channel for LiveScheduleAction.CHANGE_CHANNEL, but NOT for new MultiStreamAction.GET_CHANNEL_LOAD_METADATA
        const load = reload || data.action === 'LiveScheduleAction.CHANGE_CHANNEL';
        customMeasure.start(SMART_TAG_ADAPTER.SPA_LOAD);
        let DMAStorage = new DMAStorageService();
        let listing = data.payload.listing;
        let isNotLiveStreaming = !listing.contentCANVideo.liveStreamingUrl;
        let slug;
        if (isNotLiveStreaming) {
            slug = listing.channelSlug;
            slug =
                slug === 'cbsn-locals' && listing?.title !== undefined
                    ? listing.title.toLowerCase().replaceAll(' ', '-')
                    : slug;
        } else {
            let urlAx = listing.contentCANVideo.videoPageUrl || listing.contentCANVideo.url || '';
            if (urlAx !== '') {
                slug = urlAx.replace('live-tv/stream/', '').replace('live-tv/stream', '');
                if (slug.startsWith('channels')) {
                    slug = listing.channelSlug;
                    isNotLiveStreaming = true;
                }
            } else {
                slug = 'live-tv/stream';
            }

            /**
             * if CBS, non-MVPD user and local live in next gen
             * Force a full page refresh to get the upsell/lock slate
             */
            if (CBS.Registry.brand.name === 'CBS' && listing.contentCANVideo.status === 'PREMIUM') {
                window.location.href = '/' + slug + '/';
                return;
            }
        }
        let isLiveLocal = false;
        let replaceurl = '';
        if (
            (data.payload.channelObj.local ||
                data.payload.channelObj.streamType === SYNCBAK ||
                listing.streamType === SYNCBAK) &&
            listing.streamType !== 'mpx_live'
        ) {
            slug = listing.channelSlug;
            isLiveLocal = true;
        }
        let urlMetadata = '';
        let station_logo = '';
        let isLocalCityChannel = false;
        const tlogo =
            data?.payload?.channelObj?.filePathSmallLogoSelected ??
            data?.payload?.channelObj?.filePathSmallLogo ??
            '';
        station_logo = `${CBS.Registry.imageHost}/base/${tlogo}`;
        if (isNotLiveStreaming) {
            urlMetadata =
                CBS.Registry.region.prefix +
                '/live-tv/live-event/' +
                slug +
                '/' +
                listing.contentCANVideo.contentId +
                '/metadata.json';
            // add listing id for BBLF with multiple listings
            let listingIndex = data.payload?.listingIndex ? data.payload.listingIndex : 0;
            if (listingIndex > 0) {
                urlMetadata += '?listingIndex=' + listingIndex;
            }
            //hack for prevent legacy url structure use spa
            if (listing.contentCANVideo.url.indexOf('shows/') !== -1) {
                window.location.replace('/' + listing.contentCANVideo.url);
                return;
            }
            replaceurl = '/' + listing.contentCANVideo.url + '/';
        } else {
            if (listing.contentCANVideo.showPageUrl.indexOf('cbsn-local/') !== -1) {
                isLocalCityChannel = true;
                slug = listing.channelSlug;
            }
            if (isLiveLocal) {
                let loc = DMAStorage.getCurrentLocation();
                let listingIndex = data.payload.listingIndex ? data.payload.listingIndex : 0;
                urlMetadata =
                    CBS.Registry.region.prefix +
                    '/live-tv/channels/' +
                    slug +
                    '/metadata.json?latitude=' +
                    loc.lat +
                    '&longitude=' +
                    loc.lon;
                if (listingIndex > 0) {
                    urlMetadata += '&listingIndex=' + listingIndex;
                }
                if (CBS.Registry.brand.name == 'CBS') {
                    var mvpdCookie = jQuery.cookie('mvpd_authz');
                    if (mvpdCookie) {
                        mvpdCookie = JSON.parse(mvpdCookie);
                        if (mvpdCookie['mvpd_name']) {
                            urlMetadata += '&mvpd_name=' + mvpdCookie['mvpd_name'];
                        }
                    }
                }
            } else {
                urlMetadata =
                    CBS.Registry.region.prefix + '/live-tv/channels/' + slug + '/metadata.json';
            }
            replaceurl =
                '/' + (listing.contentCANVideo.videoPageUrl ?? listing.contentCANVideo.url) + '/';
        }
        let paramsVO = {
            CONTENT: {
                apiMetadata: [listing.contentCANVideo],
                assetURL: listing.contentCANVideo.liveStreamingUrl ?? '',
                refIdForCast: listing.contentCANVideo.contentId ?? '',
                contentId: listing.contentCANVideo.contentId ?? '',
                country: this.smartTagParams.userCountry || '',
                isLive: this.smartTagParams.isLive || false,
                drm: {},
                globalResumeTime: this.smartTagParams.isLive
                    ? NaN
                    : this.smartTagParams.grestime || 0,
                partner: this.smartTagParams.partner || '',
                autoplay: this.smartTagParams.autoPlayVid || false,
                resource: {
                    ad: {
                        ima: {
                            // extends stalled ad threshold to 30 seconds
                            stalledAdThreshold: 10,
                            // disables stalled ads check
                            enableStalledAdCheck: true,
                        },
                    },
                    playback: {
                        id3OwnerIds: [],
                    },
                    metadata: {
                        duration: this.apiMetadata[0].duration ?? 0,
                    },
                },
            },
            CONTEXT: {
                metadata: {},
            },
            TAG: {
                config: {
                    skin: [
                        {
                            SkinPluginRelatedVideos: {
                                enabled: false,
                            },
                        },
                    ],
                },
            },
        };
        return fetchData(urlMetadata)
            .then((response) => {
                return response.json();
            })
            .then((resultData) => {
                const self = this;
                clearCardsDataFromUtag();
                let container = $('.video__metadata');
                let title = container.find('p');
                let subtitle = container.find('.subTitle');
                let description = container.find('.description');
                let desc = container.find('.vodTv__data');
                let rating = container.find('.rating').html('');
                let duration = container.find('.duration').html('');
                let logo = container.find('.station__logo img');
                description.html('');
                subtitle.html('');
                if (resultData.success) {
                    if (
                        resultData.hasOwnProperty('isVpn') &&
                        (resultData.isVpn === 'isvpn' || resultData.isVpn === 'irdeto-error')
                    ) {
                        window.location.replace('/' + resultData.metaData.contentUrl);
                    }
                    paramsVO.CONTENT.channelInfo = resultData?.channelInfo;
                    self.channelInfo = paramsVO.CONTENT.channelInfo;
                    paramsVO.CONTENT.metadata = [resultData.metaData] || [{}];
                    paramsVO.CONTENT.drm = resultData.CONTENT.drm ?? {};
                    if (resultData.CONTENT.drm !== undefined) {
                        paramsVO.CONTENT.millstone = resultData.CONTENT.millstone ?? null;
                        if (resultData.CONTENT.millstone?.rulesMatched !== undefined) {
                            paramsVO.CONTEXT.metadata.rulesMatched =
                                resultData.CONTENT.millstone.rulesMatched;
                        }
                    }
                    if (resultData.gameID) {
                        paramsVO.TAG.config.skin[0]['SkinPluginRelatedVideos'] = {
                            enabled: true,
                            params: {
                                config: {
                                    contentId: resultData.gameID,
                                    type: 'RelatedVideosType.LIVE_KEY_MOMENTS',
                                    polling_interval: resultData.keymoments_polling_interval,
                                },
                            },
                        };
                    }
                    if (isLiveLocal) {
                        paramsVO.CONTENT.assetURL = resultData.assetURL ?? '';
                        paramsVO.CONTENT.tokenExpirationTime =
                            resultData?.tokenExpirationTime ?? null;
                        paramsVO.CONTENT.contentId = null;
                        delete paramsVO.CONTENT.pid;
                    } else {
                        paramsVO.CONTENT.pid = listing.contentCANVideo.pid ?? '';
                        delete paramsVO.CONTENT.assetURL;
                    }
                    paramsVO.CONTENT.metadata[0].contentBrand = resultData.metaData.brand ?? '';
                    if (paramsVO.CONTENT.resource.metadata === undefined) {
                        paramsVO.CONTENT.resource.metadata = {};
                    }
                    paramsVO.CONTENT.resource.metadata.duration =
                        listing.contentCANVideo.duration ?? 0;

                    //passing value to utag_data as mops reading the value from utag. the updated value in utag is not available for spa
                    utag_data.contentBrand = resultData.metaData.brand ?? '';
                    paramsVO.CONTENT.resource.playback.id3OwnerIds = ['www.nielsen.com'];
                    paramsVO.CONTEXT.metadata = resultData.metaData || {};
                    paramsVO.CONTEXT.metadata.airDate = resultData.metaData.airdate_iso || '';
                    paramsVO.CONTEXT.metadata.tvAirDate = resultData.metaData.airdate_tv || '';
                    paramsVO.CONTEXT.metadata.mediaType = resultData.metaData.mediaType || '';
                    paramsVO.CONTEXT.metadata.streamType = 'LIVE';
                    paramsVO.CONTEXT.metadata.showGenre =
                        listing.contentCANVideo.genre && listing.contentCANVideo.genre !== ''
                            ? listing.contentCANVideo.genre
                            : 'other';
                    if (isDVRStreamType(resultData.metaData, paramsVO.CONTENT?.channelInfo)) {
                        this.smartTagParams.streamType = paramsVO.CONTEXT.metadata.streamType =
                            'DVR';
                    }
                    // TODO: duplicate code: refactor
                    if (paramsVO.CONTEXT.metadata.streamType) {
                        paramsVO.CONTEXT.metadata.isDcrLive = paramsVO.CONTENT.isLive
                            ? ['etl', 'cbsn', 'cbs-news', 'sports_hq', 'DVR', 'LIVE'].includes(
                                  paramsVO.CONTEXT.metadata.streamType,
                              )
                            : false;
                    } else {
                        paramsVO.CONTEXT.metadata.isDcrLive = false;
                    }

                    paramsVO.CONTEXT.metadata.showSeasonNumber = listing.seasonNumber || '';
                    paramsVO.CONTEXT.metadata.showEpisodeNumber = listing.episodeNumber || '';
                    paramsVO.CONTEXT.metadata.showEpisodeTitle = listing.episodeTitle || '';
                    paramsVO.CONTEXT.metadata.primaryCategory =
                        listing.contentCANVideo.primaryCategory ?? '';
                    paramsVO.CONTEXT.metadata.copyright = listing.contentCANVideo.copyright ?? '';
                    paramsVO.CONTEXT.metadata.showId = listing.showId || '';
                    paramsVO.CONTEXT.metadata.showTitle = listing.title || '';
                    paramsVO.CONTEXT.metadata.liveVideoTitle = resultData.metaData.videoTitle || '';
                    paramsVO.CONTEXT.metadata.episodeTitle = isLiveLocal
                        ? 'Live Local TV'
                        : resultData.metaData.label || '';
                    paramsVO.CONTEXT.metadata.showSeriesId = resultData.metaData.showId || '';
                    paramsVO.CONTEXT.metadata.videoProperties =
                        resultData.metaData.videoProperties || [];
                    paramsVO.CONTEXT.metadata.videoProperties =
                        paramsVO.CONTEXT.metadata.videoProperties.join(', ');
                    paramsVO.CONTEXT.metadata.liveTvChannel = isNFL(resultData?.channelInfo)
                        ? resultData.channelInfo.channelName
                        : resultData.callSignId
                          ? 'Live Local TV'
                          : (data.payload.channelObj.channelName ??
                            data.payload.channelObj.label ??
                            '');
                    paramsVO.CONTEXT.metadata.stationCode = resultData.callSignId ?? '';
                    paramsVO.CONTEXT.metadata.userRegId = utag_data.userRegId || 'anon';
                    paramsVO.CONTEXT.metadata.userId = utag_data.userRegId || 'anon';
                    paramsVO.CONTEXT.metadata.userProfileCategory =
                        CBS.Registry.user.profile_type ?? '';
                    utag_data.rowHeaderTitle = resultData.callSignId
                        ? 'Live Local TV'
                        : (data.payload.channelObj.channelName ??
                          data.payload.channelObj.label ??
                          '');
                    paramsVO.CONTEXT.metadata.contentBrand = resultData.metaData.brand ?? '';
                    paramsVO.CONTEXT.metadata.mediaSvodContentType = listing.contentCANVideo
                        .subscriptionLevel
                        ? listing.contentCANVideo.subscriptionLevel.toLowerCase()
                        : '';
                    utag_data.mediaSvodContentType = this.smartTagParams.isPaidContent
                        ? 'paid'
                        : 'free';
                    paramsVO.CONTEXT.metadata.bundleStatus = CBS.Registry.user.bundleStatus ?? '';
                    paramsVO.CONTEXT.metadata.userStatus =
                        this.smartTagParams.userStatus || CBS.Registry.user.sb;
                    paramsVO.CONTEXT.metadata.packageStatus =
                        this.smartTagParams.packageStatus ?? CBS.Registry.user.packageStatus ?? '';
                    paramsVO.CONTEXT.metadata.vendorCode =
                        this.smartTagParams.vendorCode ?? CBS.Registry.user.vendorCode ?? '';
                    paramsVO.CONTEXT.metadata.multiPackageTracking =
                        this.smartTagParams.multiPackageTracking ?? '';
                    if (localStorage.getItem('channelBrowseCategory')) {
                        paramsVO.CONTEXT.metadata.channelBrowseCategory =
                            localStorage.getItem('channelBrowseCategory');
                        utag_data.channelBrowseCategory =
                            paramsVO.CONTEXT.metadata.channelBrowseCategory;
                    } else {
                        paramsVO.CONTEXT.metadata.channelBrowseCategory = 'all';
                        utag_data.channelBrowseCategory = 'all';
                    }
                    if (localStorage.getItem('spliceEnabled')) {
                        paramsVO.CONTEXT.metadata['spliceEnabled'] =
                            localStorage.getItem('spliceEnabled');
                        localStorage.removeItem('spliceEnabled');
                    }
                    paramsVO.CONTEXT.metadata.drmName =
                        resultData?.CONTEXT?.metadata?.drmName ?? 'none';
                    paramsVO.CONTEXT.metadata.pageTitle = document.title;
                    paramsVO.CONTEXT.metadata.currentListingTitle = resultData?.metaData
                        ?.currentListingTitle
                        ? resultData?.metaData.currentListingTitle
                        : resultData?.channelTitleName
                          ? resultData.channelTitleName
                          : resultData.metaData?.videoTitle;
                    utag_data.currentListingTitle = paramsVO.CONTEXT.metadata.currentListingTitle;
                    utag_data.pageTitle = document.title;
                    utag_data.videoTitle = resultData.metaData.videoTitle;
                    paramsVO.CONTEXT.metadata.videoTitle = resultData.metaData.videoTitle ?? '';
                    paramsVO.CONTEXT.metadata.videoId = listing.videoContentId || '';

                    const posColNum = sessionStorage.getItem('posColNum');
                    const posRowNum = sessionStorage.getItem('posRowNum');
                    paramsVO.CONTEXT.metadata.posColNum = posColNum ?? 'undefined';
                    paramsVO.CONTEXT.metadata.posRowNum = posRowNum ?? 'undefined';
                    paramsVO.CONTEXT.metadata.browserVersion = this.metaData.browserVersion;
                    paramsVO.CONTEXT.metadata.referenceProfileId = this.multiStreamPlayerID;

                    // html changes
                    logo.attr('src', station_logo);
                    logo.attr(
                        'alt',
                        (resultData.metaData.brand || resultData.metaData.contentBrand) + ' LOGO',
                    );
                    title.html(
                        resultData.channelTitleName
                            ? resultData.channelTitleName
                            : resultData.callSignId
                              ? resultData.callSignId
                              : isLocalCityChannel
                                ? resultData.metaData.showTitle // what resultData.title but incorrect for standard local channels
                                : '',
                    );
                    if (isNotLiveStreaming) {
                        title.text(resultData.metaData.showTitle ?? '');
                        subtitle.html(resultData.listing.description ?? '');
                    }
                    if (!data.payload.blockPushState) {
                        window.history.pushState('', '', replaceurl);
                    }
                    document.title = resultData.metaData.brand || resultData.metaData.contentBrand;
                    if (CBS.Registry.brand.name === 'PPLUS') {
                        document.title += ' On Paramount Plus';
                    } else {
                        document.title += ' On CBS';
                    }
                    //For checking the the video is live game
                    if (isNotLiveStreaming && data.payload.channelObj.videoContentId === '000000') {
                        rating.html(resultData.rating);
                        title.html(resultData.title);
                        subtitle.html(resultData.subtitle);
                        rating.html(resultData.raitings);
                        duration.html(resultData.duration);
                        description.html(resultData.listing.description);
                        desc.removeClass('hidden');
                    } else {
                        subtitle.html(
                            isLocalCityChannel
                                ? ''
                                : resultData.callSignId
                                  ? ''
                                  : resultData.metaData.description,
                        );
                        desc.addClass('hidden');
                    }

                    // fix/hack for VOD to Live Channels
                    if (resultData.title === 'Paramount+ Channels') {
                        title.html(resultData.listing.title ?? '');
                        container.find('.description').html('');
                        container.find('.subTitle').html(resultData.listing.description ?? '');
                    }

                    utag_data.url = document.location.href;
                    paramsVO.CONTEXT.metadata.pageType = 'video';
                    paramsVO.CONTEXT.metadata._pageType = 'video';
                    paramsVO.CONTEXT.metadata.dma = this.dma;

                    // trackState call
                    const trackStateObj = {
                        dma: paramsVO.CONTEXT.metadata.dma,
                        showGenre: paramsVO.CONTEXT.metadata.showGenre ?? '',
                        posColNum: paramsVO.CONTEXT.metadata.posColNum,
                        posRowNum: paramsVO.CONTEXT.metadata.posRowNum,
                        liveTvChannel: paramsVO.CONTEXT.metadata.liveTvChannel,
                        userRegId: paramsVO.CONTEXT.metadata.userRegId,
                        userType: utag_data.userType,
                        userStatus: this.smartTagParams.userStatus ?? '',
                        packageStatus:
                            this.smartTagParams.packageStatus ??
                            CBS.Registry.user.packageStatus ??
                            '',
                        vendorCode:
                            this.smartTagParams.vendorCode ?? CBS.Registry.user.vendorCode ?? '',
                        videoTitle: paramsVO.CONTEXT.metadata.videoTitle ?? '',
                        liveStreamStation: paramsVO.CONTEXT.metadata.stationCode ?? '',
                        userProfileId: CBS.Registry.user.profile_id ?? '',
                        userProfileMaster: CBS.Registry.user.userProfileMaster ? 'true' : 'false',
                        userProfileCategory: CBS.Registry.user.profile_type ?? '',
                        pageViewGuid: window.__page_uuid,
                        brandPlatformId: CBS.Registry.brandPlatformID || '',
                        contentBrand: resultData.metaData.brand ?? '',
                        pageType: 'video',
                        pageTitle: document.title,
                        pageUrl: document.location.href,
                        stationCode: resultData.callSignId ?? '',
                        channelBrowseCategory:
                            paramsVO.CONTEXT.metadata.channelBrowseCategory ?? 'all',
                        rowHeaderTitle: utag_data.rowHeaderTitle,
                    };
                    if (load) {
                        om.trackState(trackStateObj);
                    }
                    // end trackState call
                    if (load) {
                        this.smartTagVO.API.VIDEO.load(paramsVO);
                    }
                    if ((CBS.Registry.Features.concurrency_3_1_enabled ?? false) && load) {
                        ConcurrencyManager.concurrencyCheck(
                            listing.contentCANVideo.contentId,
                            this.smartTagVO,
                        );
                    }

                    if (load) {
                        this.smartTagVO.API.SKIN.pageAction({
                            action: 'PageAction.RELOAD_SCHEDULE',
                            data: resultData,
                        });
                    }

                    // skip if chromecast api is not available
                    if (load) {
                        if (this.GetChromecastHelper() || !Helper.isEmptyObject(this.api.CastNS)) {
                            try {
                                // set params for chromecast so it's ready to stream any moment
                                this.updateChromecastParams(listing, paramsVO);
                                if (!!data?.payload?.isSPA && this.api.CastNS.isConnected()) {
                                    this.api.CastNS.uiReady();
                                }
                            } catch (e) {
                                console.log('Failed to start cast session.', e);
                            }
                        }

                        customMeasure.end(SMART_TAG_ADAPTER.SPA_LOAD);
                        customMeasure.sendEvent({
                            eventName: SMART_TAG_ADAPTER.SPA_LOAD,
                        });
                    }

                    if (!load) {
                        return new Promise(function (resolve) {
                            resolve({ data: paramsVO });
                        });
                    }
                }
            })
            .catch(function (err) {
                console.log(err);
            });
    }

    handleMultiStreamClick(data) {
        return new Promise((resolve) => {
            this.multiStreamOpen = data?.payload?.status === 'MultiStreamStatus.OPEN';
            const contentId = this.apiMetadata?.[0]?.contentId;
            if (this.multiStreamOpen && contentId) {
                ConcurrencyManager.concurrencyCheck(contentId, this.smartTagVO);
                ConcurrencyManager.playerStateChange(ConcurrencyManager.smartTagEvent);
            }
            resolve(null);
        });
    }

    getScheduleData(data) {
        return new Promise(async (resolve, reject) => {
            try {
                const dmaService = new DMAService();
                const dmaObject = await dmaService.getDMA();
                const { latitude: lat, longitude: lon } = dmaObject;

                // Build DMA URL data directly from fresh DMA result, eliminating storage dependency
                const DMAStorage = new DMAStorageService();
                const freshDmaData = DMAStorage.getStorageDMA(); // Now guaranteed to be fresh after getDMA()
                const dmaUrlData = freshDmaData
                    ? freshDmaData.reduce((acc, dmaInfo) => {
                          if (!dmaInfo.dma || !dmaInfo.stationId) return acc;
                          const prefix = acc ? ',' : '';
                          let entry = `${prefix}${dmaInfo.dma}|${dmaInfo.stationId}|${dmaInfo.stationSlug}`;
                          if (dmaInfo.playbackUrl) entry += `|${dmaInfo.playbackUrl}`;
                          return acc + entry;
                      }, '')
                    : '';

                const bundleStatus = CBS.Registry.user.bundleStatus;
                const { payload = {} } = data || {};

                // Build URL parameters efficiently
                const params = new URLSearchParams({
                    latitude: lat,
                    longitude: lon,
                    timestamp: Date.now(),
                });
                if (bundleStatus) params.append('bundleStatus', bundleStatus);
                if (dmaUrlData) params.append('dmaInfo', dmaUrlData);
                if (payload.channelSlug) params.append('channelSlug', payload.channelSlug);
                if (payload.listingId) params.append('listingId', payload.listingId);

                const url = `${CBS.Registry.region.prefix}/live-tv/stream/liveSchedulePlayer/schedule.json?${params}`;
                const response = await fetchData(url);
                const resultData = await response.json();

                if (!resultData.success) {
                    resolve({ data: resultData });
                    return;
                }

                const channels = resultData.result.channels;
                const withChannelApi = CBS.Registry.Features?.tv_listings_with_channel_api === true;

                if (withChannelApi) {
                    // Create promises for channels needing additional listings
                    const channelPromises = channels.map((channel, index) => {
                        const needsListing =
                            !channel.currentListing &&
                            (!channel.upcomingListing || channel.upcomingListing.length === 0);

                        if (!needsListing) return Promise.resolve(null);

                        const channelUrl = `${CBS.Registry.region.prefix}/live-tv/stream/channelSchedulePlayer/${channel.slug}/schedule.json?timestamp=${Date.now()}`;
                        return fetchData(channelUrl)
                            .then((res) => res.json())
                            .then((data) =>
                                data.success ? { ...data.result, index } : { data, index },
                            );
                    });

                    const listings = await Promise.all(channelPromises);

                    // Process listings and update channels
                    listings.forEach((listing) => {
                        if (listing?.listing?.length) {
                            // Filter out live listings and take only the first future one
                            const futureListing = listing.listing
                                .filter((l) => !l.isListingLive)
                                .slice(0, 1);

                            if (futureListing.length) {
                                channels[listing.index].upcomingListing = futureListing;
                            }
                        }
                    });
                }

                resultData.result.channels = channels;
                this.scheduleAPIData = resultData.result;
                resolve({ data: resultData.result });
            } catch (error) {
                console.log('Failed to get user location or schedule data', error);
                reject('{}');
            }
        });
    }

    /**
     * Safely sets text content, handling both text and HTML appropriately
     * @param {Element} element - The DOM element to update
     * @param {string} value - The value to set
     * @param {boolean} allowHtml - Whether to allow HTML content (default: false for safety)
     */
    setElementContent(element, value, allowHtml = false) {
        if (!element || value === undefined || value === null) return;

        if (allowHtml) {
            // Only use innerHTML when explicitly allowed and needed
            // Consider implementing additional sanitization here if needed
            element.innerHTML = value;
        } else {
            // Use textContent for safety - prevents XSS
            element.textContent = value;
        }
    }

    /**
     * Updates video metadata elements based on provided data
     * @param {Object|Array} updateData - Object with element properties or array of objects
     * @param {boolean} clearFirst - Whether to clear all elements before updating (default: true)
     * @param {string} containerSelector - CSS selector for the container (default: '.video__metadata')
     * @param {Object} options - Additional options for content handling
     * @param {boolean} options.allowHtml - Whether to allow HTML in text elements (default: false)
     */
    updateVideoMetadata(
        updateData,
        clearFirst = true,
        containerSelector = '.video__metadata',
        options = {},
    ) {
        const container = document.querySelector(containerSelector);
        if (!container) {
            console.warn(`Container not found: ${containerSelector}`);
            return this;
        }

        const { allowHtml = false } = options;

        // Element selectors mapping
        const elementSelectors = {
            title: 'p',
            subtitle: '.subTitle',
            description: '.description',
            desc: '.vodTv__data',
            rating: '.rating',
            duration: '.duration',
            logo: '.station__logo img',
        };

        // Clear all elements first if requested
        if (clearFirst) {
            this.clearVideoMetadata(containerSelector);
        }

        // Handle array of update objects
        const updates = Array.isArray(updateData) ? updateData : [updateData];

        updates.forEach((data) => {
            if (!data || typeof data !== 'object') return;

            Object.keys(data).forEach((key) => {
                const selector = elementSelectors[key];
                if (!selector) return;

                const element = container.querySelector(selector);
                if (!element) return;

                const value = data[key];

                // Handle different element types
                switch (key) {
                    case 'logo':
                        // Logo is an img element, handle src and alt attributes
                        if (typeof value === 'object') {
                            if (value.src) element.setAttribute('src', value.src);
                            if (value.alt) element.setAttribute('alt', value.alt);
                        } else if (typeof value === 'string') {
                            element.setAttribute('src', value);
                        }
                        break;

                    case 'desc':
                        // Handle show/hide for a desc element
                        if (typeof value === 'boolean') {
                            if (value) {
                                element.classList.remove('hidden');
                            } else {
                                element.classList.add('hidden');
                            }
                        } else if (typeof value === 'string') {
                            this.setElementContent(element, value, allowHtml);
                        }
                        break;

                    case 'title':
                        // Always use textContent for title to prevent XSS
                        if (value !== undefined && value !== null) {
                            element.textContent = value;
                        }
                        break;

                    case 'subtitle':
                    case 'description':
                    case 'rating':
                    case 'duration':
                        // For these elements, use the safe content setter
                        if (value !== undefined && value !== null) {
                            this.setElementContent(element, value, allowHtml);
                        }
                        break;

                    default:
                        // Fallback for any other elements
                        if (value !== undefined && value !== null) {
                            this.setElementContent(element, value, allowHtml);
                        }
                        break;
                }
            });
        });

        return this;
    }

    /**
     * Clears all video metadata elements safely
     * @param {string} containerSelector - CSS selector for the container (default: '.video__metadata')
     */
    clearVideoMetadata(containerSelector = '.video__metadata') {
        const container = document.querySelector(containerSelector);
        if (!container) return this;

        // Clear text content elements using textContent (safer)
        const textElements = container.querySelectorAll(
            'p, .subTitle, .description, .rating, .duration',
        );
        textElements.forEach((element) => {
            element.textContent = '';
        });

        // Hide desc element
        const descElement = container.querySelector('.vodTv__data');
        if (descElement) {
            descElement.classList.add('hidden');
        }

        return this;
    }
}
